/**
 * Componente de Relatório para Campanhas da API Oficial do WhatsApp
 * 
 * Exibe estatísticas detalhadas, funil de conversão, cliques em botões,
 * respostas e tabela de mensagens com filtros e paginação
 */

import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';
import { exportToPdf } from '../utils/pdfExport';

interface OfficialApiReportData {
  campaign: {
    id: string;
    nome: string;
    totalContacts: number;
    sentCount: number;
    failedCount: number;
    status: string;
    criadoEm: string;
    startedAt: string;
    completedAt: string;
    caixaDisparo?: string;          // ✅ Nome da caixa de entrada
    numeroCaixaDisparo?: string;    // ✅ Número da API Oficial
    templateName?: string | null;   // ✅ Nome do template enviado
    vinculoTipo?: string | null;    // ✅ Tipo de vínculo (AGENTE ou BOT)
    vinculoId?: string | null;      // ✅ ID do agente ou bot
    vinculoName?: string | null;    // ✅ Nome do agente ou título do bot
  };
  messages: {
    total: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    pending: number;
  };
  rates: {
    deliveryRate: number;
    readRate: number;
    clickRate: number;
    replyRate: number;
  };
  buttonClicks: {
    total: number;
    uniqueClicks: number;
    byButton: Array<{
      buttonText: string;
      clicks: number;
      percentage: number;
    }>;
  };
  replies: {
    total: number;
    percentage: number;
  };
  mediaStats: {
    hasMedia: boolean;
    mediaType: string | null;
    mediaUrl: string | null;
  };
}

interface DetailedMessage {
  id: string;
  nomeLead: string;
  numeroLead: string;
  messageStatus: string;
  sentAt: string;
  deliveredAt: string;
  readAt: string;
  repliedAt: string;
  hasReply: boolean;
  errorMessage: string;
  buttonClicks: Array<{
    buttonText: string;
    clickedAt: string;
  }>;
}

interface OfficialApiCampaignReportProps {
  campaignId: string;
  onClose: () => void;
}

export function OfficialApiCampaignReport({ campaignId, onClose }: OfficialApiCampaignReportProps) {
  const { theme } = useTheme();
  const reportContainerRef = useRef<HTMLDivElement>(null);
  const [reportData, setReportData] = useState<OfficialApiReportData | null>(null);
  const [messages, setMessages] = useState<DetailedMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // Filtros e paginação
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const savedTenantId = localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (savedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = savedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Carregar relatório
  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(
        `/api/campaigns/official-api/${campaignId}/report`
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error('Erro ao carregar relatório');
      }
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  // Carregar mensagens detalhadas
  const loadMessages = async () => {
    setMessagesLoading(true);
    try {
      const response = await authenticatedFetch(
        `/api/campaigns/official-api/${campaignId}/messages?page=${currentPage}&limit=${itemsPerPage}&status=${statusFilter}`
      );

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
        setTotalPages(data.pagination.totalPages);
      } else {
        toast.error('Erro ao carregar mensagens');
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      toast.error('Erro ao carregar mensagens');
    } finally {
      setMessagesLoading(false);
    }
  };

  // Exportar CSV
  const handleExportCSV = async () => {
    try {
      toast.loading('Gerando arquivo CSV...');
      
      const response = await authenticatedFetch(
        `/api/campaigns/official-api/${campaignId}/export-csv`
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `relatorio-${campaignId}-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.dismiss();
        toast.success('Arquivo CSV baixado!');
      } else {
        toast.dismiss();
        toast.error('Erro ao exportar CSV');
      }
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.dismiss();
      toast.error('Erro ao exportar CSV');
    }
  };

  // Atualizar relatório
  const handleRefresh = () => {
    loadReport();
    loadMessages();
  };

  useEffect(() => {
    loadReport();
  }, [campaignId]);

  useEffect(() => {
    if (reportData) {
      loadMessages();
    }
  }, [currentPage, statusFilter]);

  // Funções auxiliares
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'bg-gray-100 text-gray-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'sent': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'read': return 'bg-teal-100 text-teal-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'Pendente';
      case 'accepted': return 'Aceito';
      case 'sent': return 'Enviado';
      case 'delivered': return 'Entregue';
      case 'read': return 'Lido';
      case 'failed': return 'Falhou';
      default: return status;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div className="rounded-lg p-8 max-w-md w-full mx-4 transition-colors" style={{
          backgroundColor: theme === 'dark' ? '#1D1E24' : 'white'
        }}>
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Carregando relatório...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div ref={reportContainerRef} className="rounded-lg shadow-xl w-full max-w-7xl my-8 transition-colors" style={{
        backgroundColor: theme === 'dark' ? '#1D1E24' : 'white'
      }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b transition-colors" style={{
          borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
        }}>
          <div>
            <h2 className="text-xl font-semibold text-n-slate-12">Relatório da Campanha</h2>
            <p className="text-sm mt-1 text-n-slate-11">{reportData.campaign.nome}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Atualizar
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg transition-colors"
              style={{
                backgroundColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb',
                color: theme === 'dark' ? 'white' : '#374151'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4B5563' : '#d1d5db';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3F3F46' : '#e5e7eb';
              }}
            >
              Fechar
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
          {/* Informações da Campanha */}
          <div className="rounded-lg p-4 mb-6 transition-colors" style={{
            backgroundColor: theme === 'dark' ? '#2A2A2A' : '#f9fafb'
          }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Status:</span>
                <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reportData.campaign.status)}`}>
                  {getStatusText(reportData.campaign.status)}
                </span>
              </div>
              <div>
                <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Criado em:</span>
                <span className="ml-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{formatDate(reportData.campaign.criadoEm)}</span>
              </div>
              <div>
                <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Iniciado em:</span>
                <span className="ml-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{formatDate(reportData.campaign.startedAt)}</span>
              </div>
              <div>
                <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Concluído em:</span>
                <span className="ml-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{formatDate(reportData.campaign.completedAt)}</span>
              </div>
            </div>
            {/* Segunda linha com dados adicionais da API Oficial */}
            {(reportData.campaign.caixaDisparo || reportData.campaign.numeroCaixaDisparo || reportData.campaign.templateName || reportData.campaign.vinculoName || reportData.mediaStats.mediaUrl) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4 pt-4 border-t transition-colors" style={{
                borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
              }}>
                {reportData.campaign.caixaDisparo && (
                  <div className="md:col-span-2">
                    <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>📥 Caixa de Entrada:</span>
                    <span className="ml-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{reportData.campaign.caixaDisparo}</span>
                  </div>
                )}
                {reportData.campaign.numeroCaixaDisparo && (
                  <div className="md:col-span-2">
                    <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>📱 Número API Oficial:</span>
                    <span className="ml-2 font-mono" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{reportData.campaign.numeroCaixaDisparo}</span>
                  </div>
                )}
                {reportData.campaign.templateName && (
                  <div className="md:col-span-2">
                    <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>📝 Template Enviado:</span>
                    <span className="ml-2 font-mono text-blue-600" style={{ color: theme === 'dark' ? '#60A5FA' : '#2563EB' }}>{reportData.campaign.templateName}</span>
                  </div>
                )}
                {reportData.mediaStats.mediaUrl && (
                  <div className="md:col-span-2">
                    <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>🖼️ Mídia:</span>
                    <button
                      onClick={() => window.open(reportData.mediaStats.mediaUrl!, '_blank')}
                      className="ml-2 px-3 py-1 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors inline-flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Visualizar {reportData.mediaStats.mediaType}
                    </button>
                  </div>
                )}
                {reportData.campaign.vinculoName && (
                  <div className="md:col-span-2">
                    <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>
                      {reportData.campaign.vinculoTipo === 'AGENTE' ? '🤖 Agente de IA:' : '🏷️ Bot:'}
                    </span>
                    <span className="ml-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{reportData.campaign.vinculoName}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cards de Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="rounded-lg p-4 transition-colors" style={{
              backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff',
              borderColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe'
            }}>
              <div className="text-3xl font-bold text-blue-600">{reportData.messages.total}</div>
              <div className="text-sm mt-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Total de Contatos</div>
            </div>
            <div className="rounded-lg p-4 transition-colors" style={{
              backgroundColor: theme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : '#f0fdf4',
              borderColor: theme === 'dark' ? 'rgba(34, 197, 94, 0.3)' : '#bbf7d0'
            }}>
              <div className="text-3xl font-bold text-green-600">{reportData.messages.sent}</div>
              <div className="text-sm mt-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Mensagens Enviadas</div>
            </div>
            <div className="rounded-lg p-4 transition-colors" style={{
              backgroundColor: theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2',
              borderColor: theme === 'dark' ? 'rgba(239, 68, 68, 0.3)' : '#fecaca'
            }}>
              <div className="text-3xl font-bold text-red-600">{reportData.messages.failed}</div>
              <div className="text-sm mt-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Mensagens Falhadas</div>
            </div>
            <div className="rounded-lg p-4 transition-colors" style={{
              backgroundColor: theme === 'dark' ? 'rgba(234, 179, 8, 0.1)' : '#fefce8',
              borderColor: theme === 'dark' ? 'rgba(234, 179, 8, 0.3)' : '#fde68a'
            }}>
              <div className="text-3xl font-bold text-yellow-600">{reportData.messages.pending}</div>
              <div className="text-sm mt-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Mensagens Pendentes</div>
            </div>
          </div>

          {/* Cards de Performance */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="rounded-lg p-4 transition-colors" style={{
              backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
              borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
            }}>
              <div className="text-2xl font-bold" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{reportData.rates.deliveryRate.toFixed(1)}%</div>
              <div className="text-sm mt-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Taxa de Entrega</div>
            </div>
            <div className="rounded-lg p-4 transition-colors" style={{
              backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
              borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
            }}>
              <div className="text-2xl font-bold" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{reportData.rates.readRate.toFixed(1)}%</div>
              <div className="text-sm mt-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Taxa de Leitura</div>
            </div>
            <div className="rounded-lg p-4 transition-colors" style={{
              backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
              borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
            }}>
              <div className="text-2xl font-bold" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{reportData.rates.replyRate.toFixed(1)}%</div>
              <div className="text-sm mt-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Taxa de Resposta</div>
            </div>
            <div className="rounded-lg p-4 transition-colors" style={{
              backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
              borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
            }}>
              <div className="text-2xl font-bold" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{reportData.rates.clickRate.toFixed(1)}%</div>
              <div className="text-sm mt-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Taxa de Cliques</div>
            </div>
          </div>

          {/* Funil de Conversão */}
          <div className="rounded-lg p-6 mb-6 transition-colors" style={{
            backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
            borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
          }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>Funil de Conversão</h3>
            <div className="space-y-3">
              {[
                { label: 'Enviadas', value: reportData.messages.sent, color: 'bg-blue-500', total: reportData.messages.sent },
                { label: 'Entregues', value: reportData.messages.delivered, color: 'bg-green-500', total: reportData.messages.sent },
                { label: 'Lidas', value: reportData.messages.read, color: 'bg-teal-500', total: reportData.messages.sent },
                { label: 'Responderam', value: reportData.replies.total, color: 'bg-purple-500', total: reportData.messages.sent },
                { label: 'Clicaram', value: reportData.buttonClicks.uniqueClicks, color: 'bg-indigo-500', total: reportData.messages.sent },
              ].map((item) => {
                const percentage = item.total > 0 ? (item.value / item.total) * 100 : 0;
                return (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-32 text-sm font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>{item.label}</div>
                    <div className="flex-1 rounded-full h-6 relative" style={{
                      backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb'
                    }}>
                      <div
                        className={`${item.color} h-6 rounded-full flex items-center justify-end pr-2`}
                        style={{ width: `${percentage}%` }}
                      >
                        <span className="text-white text-xs font-medium">
                          {item.value} ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Análise de Cliques em Botões */}
          {reportData.buttonClicks.total > 0 && (
            <div className="rounded-lg p-6 mb-6 transition-colors" style={{
              backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
              borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
            }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>Cliques em Botões</h3>
              <div className="space-y-3">
                {reportData.buttonClicks.byButton.map((button, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg transition-colors" style={{
                    backgroundColor: theme === 'dark' ? '#374151' : '#f9fafb'
                  }}>
                    <div>
                      <div className="font-medium" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{button.buttonText}</div>
                      <div className="text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>{button.clicks} cliques</div>
                    </div>
                    <div className="text-lg font-bold text-blue-600">{button.percentage.toFixed(1)}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Informações de Mídia */}
          {reportData.mediaStats.hasMedia && (
            <div className="rounded-lg p-6 mb-6 transition-colors" style={{
              backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
              borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
            }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>Mídia da Campanha</h3>
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  {reportData.mediaStats.mediaType === 'IMAGE' && reportData.mediaStats.mediaUrl && (
                    <img 
                      src={reportData.mediaStats.mediaUrl} 
                      alt="Mídia da campanha" 
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                  )}
                  {reportData.mediaStats.mediaType === 'VIDEO' && (
                    <div className="w-32 h-32 rounded-lg flex items-center justify-center transition-colors" style={{
                      backgroundColor: theme === 'dark' ? '#374151' : '#f3f4f6'
                    }}>
                      <svg className="w-12 h-12" style={{ color: theme === 'dark' ? '#9ca3af' : '#9ca3af' }} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                      </svg>
                    </div>
                  )}
                  {reportData.mediaStats.mediaType === 'DOCUMENT' && (
                    <div className="w-32 h-32 rounded-lg flex items-center justify-center transition-colors" style={{
                      backgroundColor: theme === 'dark' ? '#374151' : '#f3f4f6'
                    }}>
                      <svg className="w-12 h-12" style={{ color: theme === 'dark' ? '#9ca3af' : '#9ca3af' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Tipo:</div>
                  <div className="text-lg mb-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{reportData.mediaStats.mediaType}</div>
                  {reportData.mediaStats.mediaUrl && (
                    <a 
                      href={reportData.mediaStats.mediaUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Ver mídia original →
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tabela de Mensagens Detalhadas */}
          <div className="rounded-lg p-6 transition-colors" style={{
            backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
            borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
          }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>Mensagens Detalhadas</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportCSV}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Download CSV
                </button>
                <button
                  disabled={exportingPdf}
                  onClick={async () => {
                    const el = reportContainerRef.current;
                    if (!el || !reportData || exportingPdf) return;
                    setExportingPdf(true);
                    const toastId = 'pdf-export';
                    toast.loading('Gerando PDF...', { id: toastId });
                    try {
                      await exportToPdf(el, `relatorio-${reportData.campaign.nome}.pdf`);
                      toast.success('PDF gerado com sucesso!', { id: toastId });
                    } catch (err) {
                      console.error('Erro ao gerar PDF:', err);
                      toast.error('Erro ao gerar PDF', { id: toastId });
                    } finally {
                      setExportingPdf(false);
                    }
                  }}
                  className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 ${exportingPdf ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  {exportingPdf ? 'Gerando...' : 'Download PDF'}
                </button>
              </div>
            </div>

            {/* Filtros */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Filtrar por Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full md:w-64 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                style={{
                  backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                  borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                  color: theme === 'dark' ? 'white' : '#1f2937'
                }}
              >
                <option value="all">Todos</option>
                <option value="sent">Enviadas</option>
                <option value="delivered">Entregues</option>
                <option value="read">Lidas</option>
                <option value="failed">Falhadas</option>
                <option value="pending">Pendentes</option>
              </select>
            </div>

            {/* Tabela */}
            {messagesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Carregando mensagens...</span>
              </div>
            ) : messages.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y transition-colors" style={{
                    borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
                  }}>
                    <thead className="transition-colors" style={{
                      backgroundColor: theme === 'dark' ? '#374151' : '#f9fafb'
                    }}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Nome</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Telefone</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Enviado</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Entregue</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Lido</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-center" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Respondeu</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Cliques</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y transition-colors" style={{
                      backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                      borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
                    }}>
                      {messages.map((message) => (
                        <tr key={message.id} className="transition-colors" style={{
                          '--hover-bg': theme === 'dark' ? '#374151' : '#f9fafb'
                        } as React.CSSProperties}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = theme === 'dark' ? '#374151' : '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}>
                          <td className="px-4 py-3 text-sm" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{message.nomeLead || '-'}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{message.numeroLead}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(message.messageStatus)}`}>
                              {getStatusText(message.messageStatus)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>{formatDate(message.sentAt)}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>{formatDate(message.deliveredAt)}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>{formatDate(message.readAt)}</td>
                          <td className="px-4 py-3 text-center">
                            {message.hasReply ? (
                              <span className="text-green-600 font-medium" title={formatDate(message.repliedAt)}>✓</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {message.buttonClicks && message.buttonClicks.length > 0 ? (
                              <div className="space-y-1">
                                {message.buttonClicks.map((click, idx) => (
                                  <div key={idx} className="text-xs" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                                    {click.buttonText}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: theme === 'dark' ? '#6b7280' : '#9ca3af' }}>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                      Página {currentPage} de {totalPages}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb',
                          color: theme === 'dark' ? 'white' : '#374151'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== 1) {
                            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4B5563' : '#d1d5db';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== 1) {
                            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3F3F46' : '#e5e7eb';
                          }
                        }}
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb',
                          color: theme === 'dark' ? 'white' : '#374151'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== totalPages) {
                            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4B5563' : '#d1d5db';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== totalPages) {
                            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3F3F46' : '#e5e7eb';
                          }
                        }}
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                Nenhuma mensagem encontrada com os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

